import React from 'react';
import PixelIcons from '../components/PixelIcons.jsx';
import { useT } from '../i18n/useT.js';
import Arcade from '../components/arcade/Arcade.jsx';

// Hero.jsx — Retro 8-bit scene + playable arcade machine
const Hero = (() => {
  const { Heart, Coin, Star, Diamond, Arrow, Joystick } = PixelIcons;
  const { ArcadeMachine, useArcade } = Arcade;

  // ===================================================================
  // Retro pixel scene — synthwave grid + pixel sun + pixel stars
  // ===================================================================
  function RetroScene() {
    return (
      <div style={{
        position: "absolute", inset: 0, overflow: "hidden",
        zIndex: 0, pointerEvents: "none",
      }}>
        {/* Base gradient */}
        <div style={{
          position: "absolute", inset: 0,
          background:
            "linear-gradient(180deg, #07000F 0%, #15001E 40%, #2A003D 75%, #43005F 100%)",
        }}></div>

        {/* Pixel stars — generated dots via radial gradient stack */}
        <div style={{
          position: "absolute", inset: 0,
          backgroundImage:
            "radial-gradient(circle, #fff 1px, transparent 1.5px), radial-gradient(circle, #fff 1px, transparent 1.5px), radial-gradient(circle, #00E8CB 1px, transparent 1.5px), radial-gradient(circle, #FDB115 1px, transparent 1.5px)",
          backgroundSize: "120px 120px, 200px 200px, 320px 320px, 280px 280px",
          backgroundPosition: "0 0, 60px 80px, 160px 100px, 220px 50px",
          opacity: 0.5,
          animation: "starTwinkle 5s ease-in-out infinite",
        }}></div>

        {/* Pixel sun — magenta circle with horizontal cut-outs (retro outrun) */}
        <div style={{
          position: "absolute",
          left: "50%", top: "30%",
          transform: "translateX(-50%)",
          width: 280, height: 280,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, #FDB115 0%, #FD1491 50%, #43005F 100%)",
          filter: "drop-shadow(0 0 60px rgba(253,20,145,0.45))",
          mixBlendMode: "screen",
        }}>
          {/* horizontal pixel cuts */}
          {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
            <div key={i} style={{
              position: "absolute",
              left: 0, right: 0,
              top: `${55 + i * 5}%`,
              height: `${1 + i * 0.5}%`,
              background: "var(--ragross-purple-night)",
            }}></div>
          ))}
        </div>

        {/* Pixel skyline (8-bit city silhouette in cyan) */}
        <div style={{
          position: "absolute",
          left: 0, right: 0,
          bottom: "42%",
          height: 56,
          display: "flex",
          alignItems: "flex-end",
          gap: 0,
          opacity: 0.7,
        }}>
          {[18, 26, 14, 32, 10, 22, 18, 28, 12, 36, 16, 22, 30, 14, 26, 18, 32, 14, 20, 28, 16, 24, 18, 30, 14, 22, 26, 18, 32, 12].map((h, i) => (
            <div key={i} style={{
              flex: 1,
              height: h,
              background: i % 2 === 0 ? "#15001E" : "#0a0014",
              borderTop: `2px solid ${i % 3 === 0 ? "#00E8CB" : "#FD1491"}`,
              position: "relative",
            }}>
              {/* window dots */}
              {h > 18 && (
                <div style={{
                  position: "absolute",
                  top: "30%", left: "30%",
                  width: 2, height: 2,
                  background: i % 2 === 0 ? "#FDB115" : "#00E8CB",
                  boxShadow: `0 0 4px ${i % 2 === 0 ? "rgba(253,177,21,0.8)" : "rgba(0,232,203,0.8)"}`,
                }}></div>
              )}
            </div>
          ))}
        </div>

        {/* Synthwave perspective grid floor */}
        <div style={{
          position: "absolute",
          left: -200, right: -200, bottom: -120,
          height: "55%",
          perspective: "600px",
          perspectiveOrigin: "50% 0%",
        }}>
          <div style={{
            position: "absolute",
            inset: 0,
            transform: "rotateX(72deg)",
            transformOrigin: "50% 0%",
            backgroundImage:
              "linear-gradient(90deg, rgba(253,20,145,0.85) 1px, transparent 1px), linear-gradient(0deg, rgba(0,232,203,0.85) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
            animation: "gridScroll 1.6s linear infinite",
            filter: "drop-shadow(0 0 6px rgba(253,20,145,0.4))",
          }}></div>
          {/* Horizon glow */}
          <div style={{
            position: "absolute",
            left: 0, right: 0, top: 0,
            height: 80,
            background: "linear-gradient(180deg, rgba(0,232,203,0.6) 0%, transparent 100%)",
            filter: "blur(2px)",
          }}></div>
        </div>

        {/* Horizon line + scanlines */}
        <div style={{
          position: "absolute",
          left: 0, right: 0,
          top: "55%",
          height: 2,
          background: "linear-gradient(90deg, transparent, var(--ragross-cyan) 20%, var(--ragross-magenta) 50%, var(--ragross-cyan) 80%, transparent)",
          boxShadow: "0 0 24px rgba(0,232,203,0.9), 0 0 8px rgba(253,20,145,0.8)",
        }}></div>

        {/* Big retro scanlines */}
        <div style={{
          position: "absolute", inset: 0,
          background: "var(--scanlines)",
          mixBlendMode: "overlay",
          opacity: 0.55,
        }}></div>

        {/* Vignette */}
        <div style={{
          position: "absolute", inset: 0,
          background: "radial-gradient(ellipse at center, transparent 0%, rgba(11,0,20,0.4) 60%, var(--bg-app) 100%)",
        }}></div>
      </div>
    );
  }

  const heroStyles = {
    section: {
      position: "relative",
      padding: "200px 0 120px",
      overflow: "hidden",
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
    },
    container: {
      maxWidth: 1280, margin: "0 auto", padding: "0 32px",
      position: "relative", zIndex: 5,
      display: "grid",
      gridTemplateColumns: "1.05fr 0.95fr",
      gap: 64, alignItems: "center",
      width: "100%",
    },
    pressStart: {
      display: "inline-flex", alignItems: "center", gap: 12,
      padding: "10px 14px",
      background: "rgba(253,177,21,0.10)",
      border: "1px solid rgba(253,177,21,0.5)",
      fontFamily: "'Press Start 2P', monospace",
      fontSize: "10px",
      letterSpacing: "0.18em",
      color: "var(--ragross-gold)",
      width: "fit-content",
    },
    pressDot: {
      width: 8, height: 8, background: "var(--ragross-gold)",
      boxShadow: "var(--glow-gold-md)",
    },
    title: {
      font: "var(--type-display-xl)",
      letterSpacing: "-0.02em",
      lineHeight: 1.02,
      color: "#fff",
      marginTop: 24, marginBottom: 0,
    },
    titleAccent: { color: "var(--ragross-magenta)" },
    titleCyan:   { color: "var(--ragross-cyan)" },
    lead: { font: "var(--type-body-lg)", color: "var(--fg-2)", maxWidth: 540, marginTop: 24 },
    ctas:  { marginTop: 36, display: "flex", gap: 16, flexWrap: "wrap" },
    chips: { marginTop: 40, display: "flex", gap: 10, flexWrap: "wrap" },
    chip: {
      display: "inline-flex", alignItems: "center", gap: 8,
      padding: "8px 12px",
      border: "1px solid var(--border-soft)",
      background: "rgba(255,255,255,0.03)",
      borderRadius: 2,
      fontFamily: "'Press Start 2P', monospace",
      fontSize: "9px", letterSpacing: "0.10em",
      color: "var(--fg-2)",
    },

    // CRT cabinet (now holds the arcade machine)
    crt: {
      position: "relative",
      background: "var(--ragross-purple-deep)",
      border: "2px solid var(--ragross-magenta)",
      borderRadius: "var(--radius-card)",
      padding: 24,
      boxShadow: "0 0 0 4px rgba(0,0,0,0.6), 8px 8px 0 0 var(--ragross-cyan), 0 30px 100px rgba(0,0,0,0.7)",
    },
    crtBar: {
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "8px 12px",
      background: "var(--ragross-purple-night)",
      border: "1px solid rgba(255,255,255,0.08)",
      borderRadius: 2,
      fontFamily: "'Press Start 2P', monospace",
      fontSize: "9px", letterSpacing: "0.10em",
      color: "var(--fg-3)",
    },
    crtScreen: {
      position: "relative",
      marginTop: 14,
      height: 320,
      background: "radial-gradient(ellipse at center, #1a022e 0%, #07000f 80%)",
      border: "1px solid rgba(0,232,203,0.25)",
      overflow: "hidden",
    },
    crtVignette: {
      position: "absolute", inset: 0,
      pointerEvents: "none",
      boxShadow: "inset 0 0 80px rgba(0,0,0,0.7), inset 0 0 200px rgba(67,0,95,0.5)",
    },
    crtFooter: {
      marginTop: 14,
      display: "grid",
      gridTemplateColumns: "1fr 1fr 1fr",
      gap: 10,
    },
    stat: {
      padding: "10px 12px",
      background: "var(--ragross-purple-night)",
      border: "1px solid rgba(255,255,255,0.08)",
      borderRadius: 2,
      display: "flex", flexDirection: "column", gap: 6,
    },
    statKey: {
      fontFamily: "'Press Start 2P', monospace",
      fontSize: "8px", letterSpacing: "0.12em",
      color: "var(--fg-3)",
    },
    statVal: {
      fontFamily: "'VT323', monospace",
      fontSize: 26, lineHeight: 1, color: "#fff",
    },

    floater: {
      position: "absolute",
      zIndex: 4, pointerEvents: "none",
      animation: "floatY 4s ease-in-out infinite",
    },
  };

  function Hero() {
    const t = useT();
    const a = useArcade();

    return (
      <section
        id="hero"
        style={heroStyles.section}
        data-screen-label="01 Hero"
      >
        <RetroScene/>

        {/* Floating pixel decoration */}
        <div style={{ ...heroStyles.floater, top: "22%", left: "8%" }}>
          <Star size={28}/>
        </div>
        <div style={{ ...heroStyles.floater, top: "62%", left: "5%", animationDelay: "1.4s" }}>
          <Coin size={24}/>
        </div>
        <div style={{ ...heroStyles.floater, top: "18%", right: "6%", animationDelay: "0.8s" }}>
          <Diamond size={26}/>
        </div>

        <div style={heroStyles.container} className="responsive-grid-2 hero-grid">
          {/* LEFT */}
          <div>
            <h1 style={heroStyles.title} className="hero-title">
              {t("hero.title1")}{" "}
              <span style={heroStyles.titleAccent}>{t("hero.title2")}</span>
              <br />
              {t("hero.title3")}{" "}
              <span style={heroStyles.titleCyan}>{t("hero.title4")}</span>
            </h1>

            <p style={heroStyles.lead}>{t("hero.lead")}</p>

            <div style={heroStyles.ctas}>
              <a href="#portfolio" className="btn btn-primary">
                <Joystick size={12}/>
                <span>{t("hero.ctaPrimary")}</span>
              </a>
              <a href="#contact" className="btn btn-secondary">
                <span>{t("hero.ctaSecondary")}</span>
                <Arrow size={11} color="var(--ragross-cyan)"/>
              </a>
            </div>

            <div style={heroStyles.chips}>
              <span style={heroStyles.chip}><Heart size={10}/> {t("hero.chip1")}</span>
              <span style={heroStyles.chip}><Diamond size={10}/> {t("hero.chip2")}</span>
              <span style={heroStyles.chip}><Star size={10}/> {t("hero.chip3")}</span>
              <span style={heroStyles.chip}><Coin size={10}/> {t("hero.chip4")}</span>
            </div>
          </div>

          {/* RIGHT — Arcade cabinet */}
          <div style={heroStyles.crt} className="hero-arcade-cabinet">
            <div style={heroStyles.crtBar} className="hero-arcade-bar">
              <span>RAGROSS · ARCADE.EXE</span>
              <span style={{ display: "flex", gap: 6 }}>
                <span style={{ width: 8, height: 8, background: "var(--ragross-magenta)", boxShadow: "0 0 6px rgba(253,20,145,0.7)" }}></span>
                <span style={{ width: 8, height: 8, background: "var(--ragross-gold)",    boxShadow: "0 0 6px rgba(253,177,21,0.7)" }}></span>
                <span style={{ width: 8, height: 8, background: "var(--ragross-green)",   boxShadow: "0 0 6px rgba(79,253,21,0.7)" }}></span>
              </span>
            </div>

            <div style={heroStyles.crtScreen} className="hero-arcade-screen">
              <ArcadeMachine/>

              <div className="crt-scan" style={{ opacity: 0.7, zIndex: 5 }}></div>
              <div style={{ ...heroStyles.crtVignette, zIndex: 5 }}></div>
            </div>

            <div style={heroStyles.crtFooter} className="hero-arcade-stats">
              <div style={heroStyles.stat}>
                <span style={heroStyles.statKey}>{t("hero.statFounded")}</span>
                <span style={heroStyles.statVal}>2018</span>
              </div>
              <div style={heroStyles.stat}>
                <span style={heroStyles.statKey}>{t("hero.statProjects")}</span>
                <span style={{ ...heroStyles.statVal, color: "var(--ragross-magenta)" }}>30+</span>
              </div>
              <div style={heroStyles.stat}>
                <span style={heroStyles.statKey}>{t("hero.statClients")}</span>
                <span style={{ ...heroStyles.statVal, color: "var(--ragross-cyan)" }}>14</span>
              </div>
            </div>

            <div style={{ ...heroStyles.pressStart, marginTop: 18, width: "100%", justifyContent: "center" }} className="hero-press-start">
              <span style={heroStyles.pressDot}></span>
              <span>▸ {t("hero.pressStart")}</span>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return Hero;
})();

export default Hero;
